import { chain, get, includes, omit, reduce, set } from "lodash"
import moment from "moment"
import { buildContributorKey } from "./contributors"
import { computeGroupsMap } from "./assets"
import { METRIC_TYPES } from "./assets/constants"

// Hardcoded for now, will revisit in subsequent stories when these values can be parameterized
export const FACTOR_SENSITIVITY_LOOKBACK_DAYS = 125

export const FACTOR_SENSITIVITY_TYPES = {
  PREDICTED: "PREDICTED",
  HISTORICAL: "HISTORICAL",
}

export function getFactorSensitivityQueryParams(factorSensitivityType) {
  const params = {
    factorSensitivityType,
    stdDev: undefined,
    days: undefined,
    horizon: "MONTHLY",
  }

  // Hard coded values for now, will revisit in subsequent stories when these values can be parameterized
  if (factorSensitivityType === FACTOR_SENSITIVITY_TYPES.PREDICTED) {
    return {
      ...params,
      stdDev: 1.0,
    }
  }
  return {
    ...params,
    days: FACTOR_SENSITIVITY_LOOKBACK_DAYS,
  }
}

// Rebalance & Optimization query both factorSensitivity types within the same query
// These parameters being unchangeable, we can simply generate the usage string per type and we
// wont have variable name conflicts
export function factorSensitivityParametersString(type) {
  let params = getFactorSensitivityQueryParams(type)

  params = {
    ...omit(params, "factorSensitivityType"),
    type: params.factorSensitivityType,
  }

  return chain(params)
    .map((value, key) => value && `${key}: ${value}`)
    .compact()
    .join(" ")
    .value()
}

export function computeFactorSensitivityData(items, factorSensitivity) {
  return chain(factorSensitivity)
    .map((datum, index) => {
      const { date } = datum
      const result = chain(items)
        .map(item => {
          return ([
            item.id,
            item.fn
              ? item.fn(datum)
              : get(datum, item.field, 0) * 100,
          ])
        })
        .filter(([, value]) => !Number.isNaN(value))
        .fromPairs()
        .value()

      result.date = moment(date)
      result.index = index

      return result
    })
    .value()
}

export function isMetricFactorSensitivity(metricTypeId) {
  return includes(["predictedFactorSensitivity", "historicalFactorSensitivity"], metricTypeId)
}

export function getFactorSensitivityTypeFromMetric(metricTypeId) {
  return metricTypeId === "predictedFactorSensitivity" ? FACTOR_SENSITIVITY_TYPES.PREDICTED : FACTOR_SENSITIVITY_TYPES.HISTORICAL
}

export function getMetricFromFactorSensitivityType(factorSensitivityType) {
  return factorSensitivityType === FACTOR_SENSITIVITY_TYPES.PREDICTED ? "predictedFactorSensitivity" : "historicalFactorSensitivity"
}

function extractContributorFactorSensitivity(contributor) {
  return {
    sensitivity: reduce(contributor.factors, (acc, factor) => set(acc, factor.id, get(factor, "sensitivity")), {}),
  }
}

export function computeFactorSensitivityMap(factorSensitivityContributors, groupedFactorSensitivityContributors, groupingOptions) {
  const contributors = !groupingOptions ? factorSensitivityContributors : groupedFactorSensitivityContributors
  return reduce(contributors, (acc, contributor) =>
    set(acc, buildContributorKey(contributor), extractContributorFactorSensitivity(contributor)), {})
}

export function computeGroupedFactorSensitivityMap(groups) {
  return computeGroupsMap(groups, { mapFn: extractContributorFactorSensitivity })
}

export function shouldAggregateColumn(query, metricType, canAggregate = true) {
  return canAggregate && (!includes(["exposure", "sensitivity"], query) || !includes([METRIC_TYPES.Z_SCORE, METRIC_TYPES.STANDALONE], metricType))
}
